import React from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { Answer } from '../api';
import { ReportAnswers } from '../FeedbackReport';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { useReport } from 'shared/providers/ReportProvider';
import ReportSectionNote from './ReportSectionNote';
import { NOTE_QUESTION_IDS } from 'npo/pages/ReportBuilder/constants';

type ActivitiesQuestions =
  | 'serviceDelivery'
  | 'serviceSubType'
  | 'serviceSecondarySubType'
  | 'averageValueOfGoods'
  | 'frequencyOfGoods'
  | 'durationOfGoods';

const getActivitiesAnswerMap = (
  questionId: number,
): ActivitiesQuestions | undefined => {
  switch (questionId) {
    case 110001:
      return 'serviceDelivery';
    case 110002:
      return 'serviceSubType';
    case 110003:
      return 'serviceSecondarySubType';
    case 110004:
      return 'averageValueOfGoods';
    case 110005:
      return 'frequencyOfGoods';
    case 110006:
      return 'durationOfGoods';
  }
};

type ActivitiesAnswers = { [id in ActivitiesQuestions]?: Answer };

type ActivitiesRawAnswers = {
  [id: number]: Answer;
};

const getActivitiesAnswers = (
  answers: ReportAnswers,
): { rawAnswers: ActivitiesRawAnswers; activities: ActivitiesAnswers } => {
  const rawAnswers: ActivitiesRawAnswers = {};
  const activities: ActivitiesAnswers = {};
  if (!answers.activities) return { rawAnswers, activities };
  for (const a of answers.activities) {
    rawAnswers[a.dbId] = a;
    const mapQuestion = getActivitiesAnswerMap(a.question.dbId);
    if (mapQuestion) {
      activities[mapQuestion] = a;
    }
  }
  return { rawAnswers, activities };
};

interface ReportActivitiesProps {
  answers: ReportAnswers;
}

const StyledDiv = styled.div`
  background-color: ${COLORS.whiteBase};
  margin: 0 ${SPACERS.medium} ${SPACERS.medium};
  padding: ${SPACERS.medium} ${SPACERS.extraLarge};
  .activity-question {
    margin-bottom: ${SPACERS.extraTiny};
  }
  .activity-note {
    padding: 0;
    margin-top: ${SPACERS.small};
  }
`;

const ReportActivities: React.FC<ReportActivitiesProps> = ({ answers }) => {
  const {
    activities: {
      serviceDelivery,
      serviceSubType,
      serviceSecondarySubType,
      averageValueOfGoods,
      frequencyOfGoods,
      durationOfGoods,
    },
  } = useReport();

  if (!answers.activities) return null;
  const { activities } = getActivitiesAnswers(answers);

  return (
    <StyledDiv>
      <TrueImpactText variant="heading-label">ACTIVITIES</TrueImpactText>
      <TrueImpactText className="activity-question" variant="body-small">
        Service delivery method:{' '}
        <strong>{activities.serviceDelivery?.text || serviceDelivery}</strong>
      </TrueImpactText>
      <TrueImpactText className="activity-question" variant="body-small">
        Primary service activity:{' '}
        <strong>{activities.serviceSubType?.text || serviceSubType}</strong>
      </TrueImpactText>
      <TrueImpactText className="activity-question" variant="body-small">
        Secondary service activity:{' '}
        <strong>
          {activities.serviceSecondarySubType?.text || serviceSecondarySubType}
        </strong>
      </TrueImpactText>
      <TrueImpactText className="activity-question" variant="body-small">
        Average value or length of time of services delivered:{' '}
        <strong>
          {activities.averageValueOfGoods?.text || averageValueOfGoods}
        </strong>
      </TrueImpactText>
      <TrueImpactText className="activity-question" variant="body-small">
        Average number or frequency of services delivered:{' '}
        <strong>{activities.frequencyOfGoods?.text || frequencyOfGoods}</strong>
      </TrueImpactText>
      <TrueImpactText className="activity-question" variant="body-small">
        Average duration of services delivered:{' '}
        <strong>{activities.durationOfGoods?.text || durationOfGoods}</strong>
      </TrueImpactText>
      <ReportSectionNote
        className="activity-note"
        answers={answers}
        questionId={NOTE_QUESTION_IDS.activities}
        label="Activity notes:"
      />
    </StyledDiv>
  );
};

export default ReportActivities;
